// frontend/src/lib/markdown.tsx
// ─────────────────────────────────────────────────────────────────────────────
// EINE zentrale Inline-Formatierung für die GANZE App (User-Eingaben + KI-
// Fallback). Konvention (WhatsApp / alte App):
//
//   *fett*            **fett**   __fett__     → fett
//   _kursiv_                                  → kursiv
//   ~durchgestrichen~                         → durchgestrichen
//   `monospace` / `code`                      → Monospace
//   ![alt](/pfad)                             → Bild (nur Erzähltext, opt-in)
//   <b>/<strong> <i>/<em> <s>/<strike>/<del> <code>  → inline-XML (KI-Fallback)
//
// Zwei Ausgabe-Modi aus DEMSELBEN Tokenizer (garantiert identisches Verhalten):
//   renderMarkdown(text)            → fertige Nodes, Marker entfernt (Anzeige)
//   renderMarkdownWithMarkers(text) → Nodes MIT ausgegrauten Marker-Zeichen
//                                     (Live-Vorschau direkt im Eingabefeld)
//
// Boundary-Regeln (kein Lookbehind → WKWebView/iOS-kompatibel): Marker greifen
// nur an Wort-/Satzgrenzen, damit z.B. snake_case oder a*b*c NICHT formatiert
// werden.
import { Fragment, type ReactNode } from 'react';
import { assetUrl } from '../api/assets';

export interface MarkdownRenderOptions {
  /** Marker-Zeichen sichtbar lassen, aber ausgrauen (Eingabefeld-Vorschau). */
  withMarkers?: boolean;
  /** Bilder ![alt](/p) rendern (nur Erzähltext). Default false. */
  allowImages?: boolean;
  keyPrefix?: string;
}

type InlineStyle = 'strong' | 'em' | 'strike' | 'code';

type InlineNode =
  | { kind: 'text'; value: string }
  | { kind: 'image'; alt: string; src: string }
  | { kind: 'style'; style: InlineStyle; open: string; close: string; children: InlineNode[]; lead: string };

// Reihenfolge wichtig: längere Marker (**, __) vor kürzeren (*, _). XML + Code
// matchen zeichengenau; die Marker-Alternativen tragen eine führende Boundary
// (Zeilenanfang oder Whitespace/Klammer) und eine abschließende Boundary-
// Lookahead, damit Wort-interne Zeichen (snake_case, 3*4) nicht greifen.
// WICHTIG: Als Quelle (kein gemeinsames RegExp-Objekt). tokenizeInline ist
// REKURSIV — ein geteiltes /g-RegExp würde lastIndex über die Rekursionsebenen
// hinweg überschreiben → Endlosschleife/OOM. Daher pro Aufruf eine neue Instanz.
const INLINE_REGEX_SOURCE =
  String.raw`!\[([^\]]*)\]\((\/[^)\s]+)\)|<(b|strong|i|em|s|strike|del|code)>([\s\S]*?)<\/\3\s*>|` +
  '`([^`\\n]+?)`' +
  String.raw`|(^|[\s([{«„\-—])(?:\*\*([^\n]+?)\*\*|__([^\n]+?)__|\*([^*\n]+?)\*|_([^_\n]+?)_|~([^~\n]+?)~)(?=[\s.,!?:;)\]}»"'…—-]|$)`;

const XML_STYLE: Record<string, InlineStyle> = {
  b: 'strong', strong: 'strong', i: 'em', em: 'em',
  s: 'strike', strike: 'strike', del: 'strike', code: 'code',
};

export function tokenizeInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Frische RegExp-Instanz pro Aufruf (s. INLINE_REGEX_SOURCE — Rekursion!).
  const re = new RegExp(INLINE_REGEX_SOURCE, 'g');

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push({ kind: 'text', value: text.slice(lastIndex, match.index) });

    if (match[1] !== undefined && match[2]) {
      // Bild
      nodes.push({ kind: 'image', alt: match[1], src: match[2] });
    } else if (match[3]) {
      // inline-XML
      const style = XML_STYLE[match[3].toLowerCase()] ?? 'em';
      const inner = match[4] ?? '';
      nodes.push({ kind: 'style', style, open: '', close: '', lead: '', children: style === 'code' ? [{ kind: 'text', value: inner }] : tokenizeInline(inner) });
    } else if (match[5] !== undefined) {
      // `code`
      nodes.push({ kind: 'style', style: 'code', open: '`', close: '`', lead: '', children: [{ kind: 'text', value: match[5] }] });
    } else {
      // Marker-Paare mit führender Boundary (match[6])
      const lead = match[6] ?? '';
      if (match[7] !== undefined) {
        nodes.push(makeStyle('strong', '**', match[7], lead));
      } else if (match[8] !== undefined) {
        nodes.push(makeStyle('strong', '__', match[8], lead));
      } else if (match[9] !== undefined) {
        nodes.push(makeStyle('strong', '*', match[9], lead));
      } else if (match[10] !== undefined) {
        nodes.push(makeStyle('em', '_', match[10], lead));
      } else if (match[11] !== undefined) {
        nodes.push(makeStyle('strike', '~', match[11], lead));
      } else {
        nodes.push({ kind: 'text', value: match[0] });
      }
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) nodes.push({ kind: 'text', value: text.slice(lastIndex) });
  return nodes;
}

function makeStyle(style: InlineStyle, marker: string, inner: string, lead: string): InlineNode {
  return { kind: 'style', style, open: marker, close: marker, lead, children: tokenizeInline(inner) };
}

const STYLE_CLASS: Record<InlineStyle, string> = {
  strong: 'rp-inline-strong',
  em: 'rp-inline-em',
  strike: 'rp-inline-strike',
  code: 'rp-inline-code',
};

function isSafeRootRelativeImageSrc(src: string): boolean {
  if (!src.startsWith('/') || src.startsWith('//') || src.includes('\\')) return false;
  try {
    const decoded = decodeURIComponent(src);
    return decoded.startsWith('/') && !decoded.startsWith('//') && !decoded.includes('\\');
  } catch {
    return false;
  }
}

function renderNodes(nodes: InlineNode[], options: MarkdownRenderOptions, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  nodes.forEach((node, index) => {
    const key = `${keyPrefix}-${index}`;
    if (node.kind === 'text') {
      if (node.value) out.push(node.value);
      return;
    }
    if (node.kind === 'image') {
      if (options.allowImages && isSafeRootRelativeImageSrc(node.src)) {
        out.push(
          <img
            key={key}
            className="rp-scene-image"
            src={assetUrl(node.src)}
            alt={node.alt}
            loading="lazy"
            onError={(event) => { event.currentTarget.hidden = true; }}
          />,
        );
      } else {
        out.push(`![${node.alt}](${node.src})`);
      }
      return;
    }
    // Style-Knoten
    if (node.lead) out.push(node.lead);
    const marker = options.withMarkers && node.open
      ? <span className="md-marker" key={`${key}-mo`}>{node.open}</span>
      : null;
    const markerClose = options.withMarkers && node.close
      ? <span className="md-marker" key={`${key}-mc`}>{node.close}</span>
      : null;
    const inner = node.style === 'code'
      ? (node.children[0] && node.children[0].kind === 'text' ? node.children[0].value : '')
      : renderNodes(node.children, options, key);
    const cls = STYLE_CLASS[node.style];
    if (node.style === 'strong') {
      out.push(<strong className={cls} key={key}>{marker}{inner}{markerClose}</strong>);
    } else if (node.style === 'em') {
      out.push(<em className={cls} key={key}>{marker}{inner}{markerClose}</em>);
    } else if (node.style === 'strike') {
      out.push(<s className={cls} key={key}>{marker}{inner}{markerClose}</s>);
    } else {
      out.push(<code className={cls} key={key}>{marker}{inner}{markerClose}</code>);
    }
  });
  return out;
}

/** Inline-Markdown → fertige Nodes (Marker entfernt). */
export function renderMarkdownInline(text: string, options: MarkdownRenderOptions = {}): ReactNode[] {
  return renderNodes(tokenizeInline(text), options, options.keyPrefix ?? 'md');
}

/** Inline-Markdown → Nodes MIT ausgegrauten Marker-Zeichen (Eingabe-Vorschau). */
export function renderMarkdownWithMarkers(text: string, options: MarkdownRenderOptions = {}): ReactNode[] {
  return renderNodes(tokenizeInline(text), { ...options, withMarkers: true }, options.keyPrefix ?? 'mdm');
}

// ─── HTML-String-Ausgabe (für contenteditable-Eingabefeld) ───────────────────
// Erzeugt einen ESCAPTEN HTML-String mit Marker-Spans. Wird NUR aus eigenem,
// getokenisiertem Text gebaut (kein User-HTML) → sicher für innerHTML.
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const HTML_TAG: Record<InlineStyle, string> = { strong: 'strong', em: 'em', strike: 's', code: 'code' };

function nodesToHtml(nodes: InlineNode[], withMarkers: boolean): string {
  let out = '';
  for (const node of nodes) {
    if (node.kind === 'text') { out += escapeHtml(node.value); continue; }
    if (node.kind === 'image') { out += escapeHtml(`![${node.alt}](${node.src})`); continue; }
    out += escapeHtml(node.lead);
    const tag = HTML_TAG[node.style];
    const cls = STYLE_CLASS[node.style];
    const open = withMarkers && node.open ? `<span class="md-marker">${escapeHtml(node.open)}</span>` : '';
    const close = withMarkers && node.close ? `<span class="md-marker">${escapeHtml(node.close)}</span>` : '';
    const inner = node.style === 'code'
      ? escapeHtml(node.children[0] && node.children[0].kind === 'text' ? node.children[0].value : '')
      : nodesToHtml(node.children, withMarkers);
    out += `<${tag} class="${cls}">${open}${inner}${close}</${tag}>`;
  }
  return out;
}

/**
 * Inline-Markdown → escapter HTML-String MIT ausgegrauten Marker-Spans, pro Zeile
 * durch <br> getrennt. Für das contenteditable-Eingabefeld (FormattedInput).
 * Eine leere Eingabe ergibt einen leeren String.
 */
export function markdownToHtmlWithMarkers(text: string): string {
  return text
    .split('\n')
    .map((line) => nodesToHtml(tokenizeInline(line), true))
    .join('<br>');
}

/** Mehrzeiliger Text mit <br> + Inline-Markdown (für Live-Vorschau im Feld). */
export function renderMarkdownWithMarkersMultiline(text: string, keyPrefix = 'mdm'): ReactNode {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, index) => (
        <Fragment key={`${keyPrefix}-l-${index}`}>
          {index > 0 ? <br /> : null}
          {renderMarkdownWithMarkers(line, { keyPrefix: `${keyPrefix}-l-${index}` })}
        </Fragment>
      ))}
    </>
  );
}
